import { useEffect, useRef } from 'react';

import type { Stripe, StripeEmbeddedCheckout } from '@stripe/stripe-js';

export type TipJarCheckoutStatus = 'loading' | 'ready' | 'failed';

type TipJarCheckoutProps = {
  stripe: Stripe;
  clientSecret: string;
  onComplete: () => void;
  onStatusChange: (status: TipJarCheckoutStatus) => void;
};

// Mounts Stripe directly because EmbeddedCheckoutProvider drops a failed init, Stripe's only sign that checkout
// could not start. Stripe reads the options once, so a new secret or handler mounts a new checkout.
export const TipJarCheckout = ({ stripe, clientSecret, onComplete, onStatusChange }: TipJarCheckoutProps) => {
  const frameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let checkout: StripeEmbeddedCheckout | undefined;
    let isMounted = true;

    stripe
      .initEmbeddedCheckout({ clientSecret, onComplete })
      .then((embeddedCheckout) => {
        checkout = embeddedCheckout;
        if (isMounted && frameRef.current) {
          embeddedCheckout.mount(frameRef.current);
          onStatusChange('ready');
        } else {
          embeddedCheckout.destroy();
        }
      })
      .catch(() => {
        if (isMounted) onStatusChange('failed');
      });

    return () => {
      isMounted = false;
      checkout?.destroy();
    };
  }, [stripe, clientSecret, onComplete, onStatusChange]);

  return <div ref={frameRef} />;
};
